import { BusinessContact } from "@/components/policies/BusinessContact";
import { createFileRoute } from "@tanstack/react-router";
import { Mail, MessageCircle, Instagram, Facebook, Sparkles } from "lucide-react";
import { TRUWE_CONTACT, whatsappSupportUrl } from "@/lib/contact-info";

export const Route = createFileRoute("/contact")({
  component: ContactPage,
  head: () => ({
    meta: [
      { title: "Contact. TruWe" },
      {
        name: "description",
        content:
          "Talk to the TruWe team about your routine, an order, or our science-backed formulas.",
      },
      { property: "og:title", content: "Contact TruWe" },
      {
        property: "og:description",
        content: "Questions about your routine or an order? The TruWe team is here.",
      },
      { property: "og:url", content: "https://truwe.in/contact" },
    ],
    links: [{ rel: "canonical", href: "https://truwe.in/contact" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ContactPage",
          name: "Contact TruWe",
          url: "https://truwe.in/contact",
          isPartOf: { "@id": "https://truwe.in/#website" },
          mainEntity: {
            "@type": "Organization",
            "@id": "https://truwe.in/#organization",
            name: "TruWe",
            email: "hello@truwe.in",
            contactPoint: [{
              "@type": "ContactPoint",
              contactType: "customer support",
              email: "hello@truwe.in",
              availableLanguage: ["English", "Hindi"],
              areaServed: "IN",
            }],
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://truwe.in/" },
            { "@type": "ListItem", position: 2, name: "Contact", item: "https://truwe.in/contact" },
          ],
        }),
      },
    ],
  }),
});

function ContactPage() {
  return (
    <div className="mx-auto max-w-2xl px-4 py-12 md:px-8 md:py-20">
      <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-brand">Contact</p>
      <h1 className="mt-2 font-display text-4xl font-bold text-brand-deep md:text-5xl">Contact the TruWe Wellness Team.</h1>
      <p className="mt-3 text-muted-foreground">
        Questions about your routine, an order, or our formulas? Reach out, we usually reply within
        a business day.
      </p>
      <div className="mt-8 grid gap-4 sm:grid-cols-2">
        <a
          href={`mailto:${TRUWE_CONTACT.email}`}
          className="group flex items-start gap-3 rounded-2xl border border-border bg-card p-5 transition hover:-translate-y-0.5 hover:border-transparent hover:shadow-[0_10px_30px_-10px_rgba(234,67,53,0.5)]"
        >
          <span className="inline-flex h-10 w-10 flex-none items-center justify-center rounded-xl bg-gradient-to-br from-[#4285F4] via-[#EA4335] to-[#FBBC05] text-white shadow-sm">
            <Mail className="h-5 w-5" />
          </span>
          <div>
            <p className="font-display font-semibold text-brand-deep">Email</p>
            <p className="text-sm text-muted-foreground">{TRUWE_CONTACT.email}</p>
          </div>
        </a>
        <a
          href={whatsappSupportUrl("Hi TruWe, I have a question.")}
          target="_blank"
          rel="noreferrer"
          className="group flex items-start gap-3 rounded-2xl border border-border bg-card p-5 transition hover:-translate-y-0.5 hover:border-transparent hover:shadow-[0_10px_30px_-10px_rgba(37,211,102,0.55)]"
        >
          <span className="inline-flex h-10 w-10 flex-none items-center justify-center rounded-xl bg-[#25D366] text-white shadow-sm">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden="true">
              <path d="M20.52 3.48A11.85 11.85 0 0 0 12.05 0C5.5 0 .2 5.3.2 11.85c0 2.09.55 4.13 1.6 5.93L0 24l6.4-1.68a11.83 11.83 0 0 0 5.64 1.44h.01c6.55 0 11.85-5.3 11.85-11.85 0-3.17-1.23-6.15-3.38-8.43ZM12.05 21.4h-.01a9.55 9.55 0 0 1-4.87-1.33l-.35-.21-3.8 1 1.01-3.7-.23-.38a9.53 9.53 0 0 1-1.46-5.03c0-5.27 4.29-9.56 9.57-9.56 2.55 0 4.95.99 6.76 2.8a9.5 9.5 0 0 1 2.8 6.76c0 5.28-4.3 9.57-9.57 9.57Zm5.24-7.16c-.29-.14-1.7-.84-1.97-.94-.26-.1-.46-.14-.65.15-.19.29-.74.94-.91 1.13-.17.19-.34.22-.63.07-.29-.14-1.22-.45-2.32-1.43-.86-.77-1.44-1.71-1.61-2-.17-.29-.02-.44.13-.58.13-.13.29-.34.43-.5.15-.17.19-.29.29-.48.1-.19.05-.36-.02-.5-.07-.14-.65-1.57-.9-2.15-.24-.57-.48-.49-.65-.5-.17-.01-.36-.01-.55-.01a1.06 1.06 0 0 0-.77.36c-.26.29-1 .98-1 2.4s1.03 2.78 1.17 2.97c.14.19 2.02 3.09 4.9 4.33.68.29 1.22.47 1.63.6.68.22 1.31.19 1.8.11.55-.08 1.7-.69 1.94-1.36.24-.67.24-1.24.17-1.36-.07-.12-.26-.19-.55-.33Z" />
            </svg>
          </span>
          <div>
            <p className="font-display font-semibold text-brand-deep">WhatsApp</p>
            <p className="text-sm text-muted-foreground">Chat with our team</p>
          </div>
        </a>
        <a
          href="https://www.instagram.com/truweofficial/"
          target="_blank"
          rel="noreferrer"
          className="group flex items-start gap-3 rounded-2xl border border-border bg-card p-5 transition hover:-translate-y-0.5 hover:border-transparent hover:shadow-[0_10px_30px_-10px_rgba(220,39,67,0.55)]"
        >
          <span className="inline-flex h-10 w-10 flex-none items-center justify-center rounded-xl text-white shadow-sm [background-image:linear-gradient(45deg,#f09433_0%,#e6683c_25%,#dc2743_50%,#cc2366_75%,#bc1888_100%)]">
            <Instagram className="h-5 w-5" />
          </span>
          <div>
            <p className="font-display font-semibold text-brand-deep">Instagram</p>
            <p className="text-sm text-muted-foreground">@truweofficial</p>
          </div>
        </a>
        <a
          href="https://www.facebook.com/profile.php?id=61591463848208"
          target="_blank"
          rel="noreferrer"
          className="group flex items-start gap-3 rounded-2xl border border-border bg-card p-5 transition hover:-translate-y-0.5 hover:border-transparent hover:shadow-[0_10px_30px_-10px_rgba(24,119,242,0.55)]"
        >
          <span className="inline-flex h-10 w-10 flex-none items-center justify-center rounded-xl bg-[#1877F2] text-white shadow-sm">
            <Facebook className="h-5 w-5 fill-current" />
          </span>
          <div>
            <p className="font-display font-semibold text-brand-deep">Facebook</p>
            <p className="text-sm text-muted-foreground">Follow our page</p>
          </div>
        </a>
        <button
          type="button"
          onClick={() => window.dispatchEvent(new Event("vita:open"))}
          className="group flex items-start gap-3 rounded-2xl border border-border bg-card p-5 text-left transition hover:-translate-y-0.5 hover:border-transparent hover:shadow-[0_10px_30px_-10px_rgba(30,94,255,0.55)] sm:col-span-2"
        >
          <span className="inline-flex h-10 w-10 flex-none items-center justify-center rounded-xl bg-gradient-to-br from-brand to-brand-deep text-white shadow-sm">
            <Sparkles className="h-5 w-5" />
          </span>
          <div>
            <p className="font-display font-semibold text-brand-deep">Ask Vita</p>
            <p className="text-sm text-muted-foreground">Instant answers from our wellness AI</p>
          </div>
        </button>
      </div>
      <BusinessContact />
    </div>
  );
}
